import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCcw, ExternalLink, Save, Check, X, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight, Trash2 /*, ShoppingCart, Loader2 */ } from 'lucide-react';

const ImageModal = ({ isOpen, onClose, imageUrl, imageUrls, items }) => {
  const colors = [
    'border-red-500 bg-red-500',
    'border-blue-500 bg-blue-500',
    'border-green-500 bg-green-500',
    'border-yellow-500 bg-yellow-500',
    'border-purple-500 bg-purple-500',
    'border-orange-500 bg-orange-500',
    'border-pink-500 bg-pink-500',
    'border-cyan-500 bg-cyan-500',
    'border-indigo-500 bg-indigo-500',
    'border-teal-500 bg-teal-500',
  ];
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (isOpen && imageUrls && imageUrl) {
      const getPath = (url) => url ? url.split('?')[0] : '';
      const targetPath = getPath(imageUrl);
      const idx = imageUrls.findIndex(url => getPath(url) === targetPath);
      if (idx !== -1) setCurrentIndex(idx);
    } else if (!isOpen) {
      setCurrentIndex(0);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [isOpen, imageUrl, imageUrls]);

  if (!isOpen) return null;

  const currentUrl = imageUrls && imageUrls.length > 0 ? imageUrls[currentIndex] : imageUrl;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 5));
  const handleZoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.2, 0.5);
      if (newZoom === 1) setOffset({ x: 0, y: 0 });
      return newZoom;
    });
  };
  const resetZoom = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (imageUrls && currentIndex < imageUrls.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetZoom();
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (imageUrls && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetZoom();
    }
  };

  // If items is a single object, wrap it in an array for consistent mapping
  const itemsList = Array.isArray(items) ? items : (items ? [items] : []);
  let title = itemsList.length === 1 ? itemsList[0].item_name : `Multiple Items (${itemsList.length})`;

  // If we have specific labels for the current image index, prioritize it as the title
  if (itemsList.length === 1 && itemsList[0].bounding_boxes && Array.isArray(itemsList[0].bounding_boxes)) {
    const bbEntry = itemsList[0].bounding_boxes[currentIndex];
    if (bbEntry && typeof bbEntry === 'object' && bbEntry.label) {
      title = bbEntry.label;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-1 sm:p-4" onClick={onClose}>
      <div className="relative max-w-5xl w-full bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col h-[95vh] sm:h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-2 sm:p-4 border-b bg-white z-10">
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <h3 className="text-sm sm:text-lg font-semibold text-slate-800 line-clamp-1">{title} {imageUrls && imageUrls.length > 1 ? `(${currentIndex + 1}/${imageUrls.length})` : ''}</h3>
            <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
              <button 
                onClick={handleZoomOut}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600"
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              <span className="text-xs font-mono w-12 text-center text-slate-500">{Math.round(zoom * 100)}%</span>
              <button 
                onClick={handleZoomIn}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600"
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
              <button 
                onClick={resetZoom}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600 ml-1 border-l pl-2"
                title="Reset Zoom"
              >
                <RotateCcw size={18} />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full transition-colors">
            <X size={24} className="text-slate-500" />
          </button>
        </div>
        
        <div 
          className={`relative flex-1 overflow-hidden bg-slate-900 flex items-center justify-center ${zoom > 1 ? 'cursor-grab' : 'cursor-default'} ${isDragging ? 'cursor-grabbing' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div 
            className="relative transition-transform duration-200 ease-out origin-center"
            style={{ 
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            }}
          >
            <img 
              src={currentUrl} 
              alt={title} 
              className="max-w-full max-h-[70vh] w-auto h-auto block select-none pointer-events-none" 
            />
            {itemsList.map((item, idx) => {
              const colorClass = colors[idx % colors.length];
              const [borderColor, bgColor] = colorClass.split(' ');
              
              let currentBox = item.bounding_box;
              let currentLabel = item.item_name;

              // Check if we have multiple bounding boxes (from test results or updated schema)
              if (item.bounding_boxes && Array.isArray(item.bounding_boxes)) {
                const bbEntry = item.bounding_boxes[currentIndex];
                if (bbEntry && typeof bbEntry === 'object' && bbEntry.box) {
                  currentBox = bbEntry.box;
                  currentLabel = bbEntry.label || currentLabel;
                } else if (bbEntry) {
                  currentBox = bbEntry;
                }
              } else if (currentIndex !== 0) {
                currentBox = null; // Default bounding box only applies to first image
              }

              // Ignore [0,0,0,0] boxes which might be returned by some models despite instructions
              if (Array.isArray(currentBox) && currentBox.every(val => val === 0)) {
                currentBox = null;
              }

              return currentBox && (
                <div 
                  key={`bb-${item.id || idx}`}
                  className={`absolute border-2 ${borderColor} ${bgColor} bg-opacity-10 pointer-events-none`}
                  style={{
                    top: `${currentBox[0] / 10}%`,
                    left: `${currentBox[1] / 10}%`,
                    height: `${(currentBox[2] - currentBox[0]) / 10}%`,
                    width: `${(currentBox[3] - currentBox[1]) / 10}%`,
                  }}
                >
                  <span className={`absolute -top-6 left-0 ${bgColor} text-white text-[10px] px-1 rounded whitespace-nowrap shadow-sm`}>
                    {currentLabel}
                  </span>
                </div>
              );
            })}
          </div>

          {imageUrls && currentIndex > 0 && (
            <button 
              onClick={prevImage}
              className="absolute left-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all shadow-lg"
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {imageUrls && currentIndex < imageUrls.length - 1 && (
            <button 
              onClick={nextImage}
              className="absolute right-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all shadow-lg"
            >
              <ChevronRight size={32} />
            </button>
          )}
        </div>

        {imageUrls && imageUrls.length > 1 && (
          <div className="p-2 bg-slate-800 flex justify-center gap-2 overflow-x-auto border-t border-slate-700">
            {imageUrls.map((url, idx) => (
              <img 
                key={idx}
                src={url}
                onClick={() => { setCurrentIndex(idx); resetZoom(); }}
                className={`w-12 h-12 object-cover rounded cursor-pointer transition-all ${currentIndex === idx ? 'ring-2 ring-blue-500 scale-105' : 'opacity-60 hover:opacity-100'}`}
                alt={`Thumbnail ${idx + 1}`}
              />
            ))}
          </div>
        )}

        <div className="p-2 sm:p-4 bg-slate-50 border-t text-xs text-slate-500 overflow-x-auto max-h-24 sm:max-h-32 shrink-0">
          {itemsList.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
              {itemsList.map((item, idx) => {
                const colorClass = colors[idx % colors.length];
                const bgColor = colorClass.split(' ')[1];
                return (
                  <div key={`info-${item.id || idx}`} className="truncate">
                    <span className={`inline-block w-2 h-2 rounded-full ${bgColor} mr-1`}></span>
                    <strong className="text-slate-700">{item.item_name}:</strong> {item.bounding_box ? `[${item.bounding_box.join(', ')}]` : 'No bounding box'}
                  </div>
                );
              })}
            </div>
          ) : 'No items available'}
        </div>
      </div>
    </div>
  );
};

const HistoryPage = () => {
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  // const [updatingNameId, setUpdatingNameId] = useState(null);  // TEMPORARILY DISABLED
  const [selectedItems, setSelectedItems] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageUrls, setSelectedImageUrls] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  // const [ebaySearchingGroup, setEbaySearchingGroup] = useState(null);  // TEMPORARILY DISABLED
  // const [ebayResultsMap, setEbayResultsMap] = useState({});  // TEMPORARILY DISABLED

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/get_items');
      setItems(response.data);
      setOriginalItems(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  const flushHistory = async () => {
    if (!window.confirm("Are you sure you want to clear all analysis history and blob storage? This cannot be undone.")) return;
    
    try {
      const response = await axios.post('/flush_history');
      if (response.data.status === 'success') {
        setItems([]);
        setOriginalItems([]);
        setCurrentPage(1);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Error flushing history:", error);
      alert("Failed to flush history.");
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // TEMPORARILY DISABLED - searchEbayForGroup
  // const searchEbayForGroup = async (group) => {
  //   const groupKey = `${group.blob_url}-${group.timestamp}`;
  //   setEbaySearchingGroup(groupKey);
  //   try {
  //     const itemIds = group.items.filter(i => i.id).map(i => i.id);
  //     const response = await axios.post('/ebay_search', {
  //       blob_urls: group.blob_urls || [group.blob_url],
  //       item_ids: itemIds
  //     });
  //     if (response.data.status === 'success') {
  //       setEbayResultsMap(prev => ({ ...prev, [groupKey]: response.data.ebay_results }));
  //       const ebayJson = response.data.ebay_results;
  //       setItems(prev => prev.map(item => 
  //         itemIds.includes(item.id) ? { ...item, ebay_results: ebayJson } : item
  //       ));
  //     }
  //   } catch (error) {
  //     console.error('Error searching eBay:', error);
  //   } finally {
  //     setEbaySearchingGroup(null);
  //   }
  // };

  const handlePriceChange = (id, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, actual_price: value } : item
    ));
  };

  // TEMPORARILY DISABLED - handleManualNameChange and updateItemName
  // const handleManualNameChange = (id, value) => {
  //   setItems(items.map(item => 
  //     item.id === id ? { ...item, manual_name: value } : item
  //   ));
  // };
  // const updateItemName = async (id, name) => {
  //   setUpdatingNameId(id);
  //   try {
  //     const response = await axios.post('/update_item_name', { id, item_name: name });
  //     if (response.data.status === 'success') {
  //       setOriginalItems(originalItems.map(item => 
  //         item.id === id ? { ...item, manual_name: name } : item
  //       ));
  //     } else { alert("Error: " + response.data.message); }
  //   } catch (error) {
  //     console.error("Error updating item name:", error);
  //     alert("Error updating item name. Please try again.");
  //   } finally { setUpdatingNameId(null); }
  // };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      const response = await axios.post('/update_price', {
        id: id,
        actual_price: parseFloat(price)
      });
      if (response.data.status === 'success') {
        // Update originalItems to mark as saved
        setOriginalItems(originalItems.map(item => 
          item.id === id ? { ...item, actual_price: parseFloat(price) } : item
        ));
      } else {
        alert("Error: " + response.data.message);
      }
    } catch (error) {
      console.error("Error updating price:", error);
      alert("Error updating price. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const groupItems = (items) => {
    const getPath = (url) => url ? url.split('?')[0] : '';
    const groups = [];
    items.forEach(item => {
      // Find an existing group with the same timestamp (within 5 seconds)
      // and matching first image to group multi-image uploads
      const group = groups.find(g => {
        const timeDiff = Math.abs(new Date(g.timestamp) - new Date(item.timestamp));
        const sameFirstImage = getPath(g.blob_url) === getPath(item.blob_url);
        const sameFirstImagePlural = g.blob_urls && item.blob_urls && getPath(g.blob_urls[0]) === getPath(item.blob_urls[0]);
        return timeDiff < 5000 && (sameFirstImage || sameFirstImagePlural);
      });
      
      if (group) {
        group.items.push(item);
      } else {
        groups.push({
          blob_url: item.blob_url,
          blob_urls: item.blob_urls,
          timestamp: item.timestamp,
          model_name: item.model_name,
          analysis_duration_ms: item.analysis_duration_ms,
          analysis_cost: item.analysis_cost,
          items: [item]
        });
      }
    });
    return groups;
  };

  const groupedItems = groupItems(items);
  
  // Pagination logic
  const totalPages = Math.ceil(groupedItems.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGroups = groupedItems.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-8 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Auction Item History</h1>
        <div className="flex gap-2 sm:gap-4">
          <button 
            onClick={fetchItems}
            className="flex items-center px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors text-sm sm:text-base"
          >
            <RefreshCcw size={16} className={`mr-1.5 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button 
            onClick={flushHistory}
            disabled={items.length === 0}
            className={`flex items-center px-3 sm:px-4 py-2 rounded text-white transition-colors text-sm sm:text-base ${
              items.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
            }`}
            title="Clear all analysis history"
          >
            <Trash2 size={16} className="mr-1.5 sm:mr-2" />
            Flush
          </button>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-8">
        {currentGroups.map((group, gIdx) => (
          <div key={`${group.blob_url}-${group.timestamp}`} className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-slate-50 p-3 sm:p-4 border-b border-slate-200 flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="relative group cursor-pointer" onClick={() => {
                setSelectedImageUrl(group.blob_url);
                setSelectedImageUrls(group.blob_urls);
                setSelectedItems(group.items);
              }}>
                {group.blob_url ? (
                  <div className="flex -space-x-4">
                    {(group.blob_urls || [group.blob_url]).slice(0, 3).map((url, idx) => (
                      <div key={idx} className="relative" style={{ zIndex: 30 - idx * 10 }}>
                        <img 
                          src={url} 
                          alt={`Analysis ${idx}`} 
                          className="w-14 h-14 sm:w-20 sm:h-20 object-cover rounded border-2 border-white shadow-sm hover:scale-105 transition-transform" 
                        />
                        {idx === 0 && (group.blob_urls || []).length > 1 && (
                          <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-md z-40 border border-white">
                            {(group.blob_urls || []).length}
                          </span>
                        )}
                      </div>
                    ))}
                    {(group.blob_urls || []).length > 3 && (
                      <div className="w-14 h-14 sm:w-20 sm:h-20 bg-slate-800/50 rounded flex items-center justify-center text-white font-bold backdrop-blur-sm border-2 border-white text-sm" style={{ zIndex: 0 }}>
                        +{(group.blob_urls).length - 3}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-14 h-14 sm:w-20 sm:h-20 bg-slate-200 rounded flex items-center justify-center text-slate-400 italic text-xs border border-slate-300">N/A</div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all rounded">
                  <ExternalLink size={16} className="text-white opacity-0 group-hover:opacity-100" />
                </div>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-semibold text-slate-700">{new Date(group.timestamp).toLocaleString()}</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-[10px] font-mono text-slate-500 bg-white border px-2 py-0.5 rounded">{group.model_name}</span>
                  <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
                    {group.analysis_duration_ms !== undefined && group.analysis_duration_ms !== null 
                      ? `${Number(group.analysis_duration_ms).toFixed(0)}ms` 
                      : 'N/A'}
                  </span>
                  <span className="text-[10px] font-mono text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">
                    {group.analysis_cost !== undefined && group.analysis_cost !== null 
                      ? `$${Number(group.analysis_cost).toFixed(4)}` 
                      : 'N/A'}
                  </span>
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Items Found</div>
                <div className="text-2xl font-bold text-slate-800">{group.items.length}</div>
              </div>
            </div>

            {/* Desktop table view */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-white border-b border-slate-100">
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">AI Name</th>
                    {/* <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Manual Name</th> TEMPORARILY DISABLED */}
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Est. Value</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Actual Price</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-1/4">Margin Analysis</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const originalItem = originalItems.find(oi => oi.id === item.id);
                    const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
                    const isUpdating = updatingId === item.id;

                    return (
                      <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors last:border-0">
                        <td className="p-4 text-sm font-medium text-slate-700">
                          <button 
                            onClick={() => {
                              setSelectedImageUrl(item.blob_url);
                              setSelectedImageUrls(item.blob_urls || [item.blob_url]);
                              setSelectedItems(item);
                            }}
                            className="hover:text-blue-600 hover:underline transition-colors text-left"
                          >
                            {item.item_name}
                          </button>
                        </td>
                        {/* TEMPORARILY DISABLED - Manual Name column */}
                        <td className="p-4 text-sm text-green-600 font-bold text-right">${item.estimated_value?.toLocaleString()}</td>
                        <td className="p-4 text-sm">
                          <div className="flex items-center">
                            <div className="relative flex items-center">
                              <span className="absolute left-2 text-slate-400 text-sm">$</span>
                              <input 
                                type="number" 
                                placeholder="0.00"
                                value={item.actual_price} 
                                onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                                  isDirty 
                                    ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                                    : 'border-slate-200 focus:ring-green-500'
                                }`}
                                step="0.01"
                              />
                              <button 
                                onClick={() => updatePrice(item.id, item.actual_price)}
                                disabled={!isDirty || isUpdating}
                                className={`absolute right-1 p-1 rounded transition-all ${
                                  isUpdating
                                    ? 'text-slate-400'
                                    : isDirty
                                      ? 'text-blue-600 hover:bg-blue-100'
                                      : 'text-green-500 cursor-default'
                                }`}
                                title={isDirty ? "Save Price" : "Price Saved"}
                              >
                                {isUpdating ? (
                                  <RefreshCcw size={14} className="animate-spin" />
                                ) : isDirty ? (
                                  <Save size={14} />
                                ) : (
                                  <Check size={14} />
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-xs text-slate-500 italic">{item.margin_analysis}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* TEMPORARILY DISABLED - eBay Similar Items Section (Desktop) */}

            {/* Mobile card view */}
            <div className="sm:hidden divide-y divide-slate-100">
              {group.items.map((item) => {
                const originalItem = originalItems.find(oi => oi.id === item.id);
                const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
                const isUpdating = updatingId === item.id;

                return (
                  <div key={item.id} className="p-3 space-y-2">
                    <button 
                      onClick={() => {
                        setSelectedImageUrl(item.blob_url);
                        setSelectedImageUrls(item.blob_urls || [item.blob_url]);
                        setSelectedItems(item);
                      }}
                      className="text-sm font-medium text-slate-700 hover:text-blue-600 hover:underline transition-colors text-left"
                    >
                      {item.item_name}
                    </button>
                    {/* TEMPORARILY DISABLED - Manual Name Input (Mobile) */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-green-600 font-bold">${item.estimated_value?.toLocaleString()}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-500">Actual:</span>
                        <div className="relative flex items-center">
                          <span className="absolute left-2 text-slate-400 text-sm">$</span>
                          <input 
                            type="number" 
                            placeholder="0.00"
                            value={item.actual_price} 
                            onChange={(e) => handlePriceChange(item.id, e.target.value)}
                            className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                              isDirty 
                                ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                                : 'border-slate-200 focus:ring-green-500'
                            }`}
                            step="0.01"
                          />
                          <button 
                            onClick={() => updatePrice(item.id, item.actual_price)}
                            disabled={!isDirty || isUpdating}
                            className={`absolute right-1 p-1 rounded transition-all ${
                              isUpdating
                                ? 'text-slate-400'
                                : isDirty
                                  ? 'text-blue-600 hover:bg-blue-100'
                                  : 'text-green-500 cursor-default'
                            }`}
                          >
                            {isUpdating ? (
                              <RefreshCcw size={14} className="animate-spin" />
                            ) : isDirty ? (
                              <Save size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    {item.margin_analysis && (
                      <p className="text-xs text-slate-500 italic">{item.margin_analysis}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* TEMPORARILY DISABLED - eBay Similar Items (Mobile) */}
          </div>
        ))}

        {items.length === 0 && !loading && (
          <div className="p-12 text-center text-slate-400 border-2 border-dashed rounded-lg bg-slate-50">
            <p className="italic">No analysis history found.</p>
          </div>
        )}

        {groupedItems.length > itemsPerPage && (
          <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-slate-100 gap-4">
            <div className="text-sm text-slate-500">
              Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, groupedItems.length)}</span> of <span className="font-medium">{groupedItems.length}</span> analysis sessions
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center mr-4">
                <span className="text-sm text-slate-500 mr-2">Show:</span>
                <select 
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-slate-200 rounded px-2 py-1 outline-none focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <nav className="flex items-center gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded border ${currentPage === 1 ? 'text-slate-300 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  <ChevronLeft size={16} />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show current page, first, last, and pages around current
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-slate-400">...</span>
                      )}
                      <button
                        onClick={() => paginate(page)}
                        className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${
                          currentPage === page 
                            ? 'bg-blue-600 text-white' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded border ${currentPage === totalPages ? 'text-slate-300 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  <ChevronRight size={16} />
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      <ImageModal 
        isOpen={!!selectedItems} 
        onClose={() => {
          setSelectedItems(null);
          setSelectedImageUrl(null);
          setSelectedImageUrls(null);
        }}
        imageUrl={selectedImageUrl}
        imageUrls={selectedImageUrls}
        items={selectedItems}
      />
    </div>
  );
};

export default HistoryPage;
